// Column + endpoint configs for the generic ReportTable component.
// Each report reads from existing sales/purchase/inventory tables only — see the
// "Accounting Reports" build notes for what each report means and what it can't show yet.

export const stockSummaryConfig = {
  title: "Stock Summary",
  endpoint: "/api/v1/reports/stock-summary",
  dateFilter: "none",
  subtitle: "Current on-hand quantity per product, valued at the price picked: the most recent purchase price, or the selling price.",
  filters: ["priceBasis"],
  columns: [
    { key: "skuCode", label: "SKU" },
    { key: "productName", label: "Product" },
    { key: "totalQuantity", label: "Quantity", format: "qty" },
    { key: "estimatedUnitCost", label: "Unit Price", format: "money", total: false },
    { key: "estimatedValue", label: "Est. Value", format: "money" },
  ],
};

export const stockMovementConfig = {
  title: "Stock Movement",
  endpoint: "/api/v1/reports/stock-movement",
  dateFilter: "required",
  subtitle: "Opening/closing stock per product across a date range, built from the stock ledger.",
  columns: [
    { key: "skuCode", label: "SKU" },
    { key: "productName", label: "Product" },
    { key: "openingQuantity", label: "Opening", format: "qty" },
    { key: "inQuantity", label: "In", format: "qty" },
    { key: "outQuantity", label: "Out", format: "qty" },
    { key: "closingQuantity", label: "Closing", format: "qty" },
  ],
};

export const salesRegisterConfig = {
  title: "Sales Register",
  endpoint: "/api/v1/reports/sales-register",
  dateFilter: "optional",
  filters: ["salesmanBeats", "customer"],
  subtitle:
    "Every sales invoice, newest first, with its basic amount, GST split, scheme and cash discount, total and round-off (1012.60 is rounded to 1013, round off +0.40). Leave dates blank to see all invoices.",
  columns: [
    { key: "invoiceDate", label: "Invoice Date" },
    { key: "customerName", label: "Customer Name" },
    { key: "invoiceNumber", label: "Invoice #" },
    { key: "basicAmount", label: "Basic Amount (before tax)", format: "money" },
    { key: "cgstAmount", label: "CGST", format: "money" },
    { key: "sgstAmount", label: "SGST", format: "money" },
    { key: "igstAmount", label: "IGST", format: "money" },
    { key: "schemeDiscount", label: "Scheme Discount", format: "money" },
    { key: "cashDiscount", label: "Cash Discount", format: "money" },
    { key: "totalAmount", label: "Total (incl. tax)", format: "money" },
    { key: "roundOff", label: "Round Off", format: "signedMoney" },
    { key: "roundedTotal", label: "Rounded Total", format: "money" },
    { key: "salesmanName", label: "Salesman" },
    { key: "beatName", label: "Beat" },
    { key: "status", label: "Status" },
    { key: "amountCollected", label: "Collected", format: "money" },
  ],
};

export const purchaseRegisterConfig = {
  title: "Purchase Register",
  endpoint: "/api/v1/reports/purchase-register",
  dateFilter: "optional",
  subtitle: "Every purchase invoice, newest first. Leave dates blank to see all invoices.",
  columns: [
    { key: "brandInvoiceNumber", label: "Invoice #" },
    { key: "invoiceDate", label: "Date" },
    { key: "brandName", label: "Brand" },
    { key: "status", label: "Status" },
    { key: "totalAmount", label: "Total", format: "money" },
  ],
};

export const itemWiseSalesConfig = {
  title: "Product Wise Sales",
  endpoint: "/api/v1/reports/item-wise-sales",
  dateFilter: "optional",
  subtitle: "Quantity and revenue sold per product, highest revenue first.",
  columns: [
    { key: "skuCode", label: "SKU" },
    { key: "productName", label: "Product" },
    { key: "quantitySold", label: "Qty Sold", format: "qty" },
    { key: "revenue", label: "Revenue", format: "money" },
  ],
};

export const customerWiseSalesConfig = {
  title: "Customer-wise Sales",
  endpoint: "/api/v1/reports/customer-wise-sales",
  dateFilter: "optional",
  subtitle: "Sales per customer, highest first: basic amount, GST split, scheme and cash discount, total and round-off over the customer's invoices.",
  columns: [
    { key: "customerCode", label: "Customer Code" },
    { key: "customerName", label: "Customer Name" },
    { key: "firstInvoiceDate", label: "First Invoice Date" },
    { key: "lastInvoiceDate", label: "Last Invoice Date" },
    { key: "invoiceCount", label: "Invoices" },
    { key: "basicAmount", label: "Basic Amount (before tax)", format: "money" },
    { key: "cgstAmount", label: "CGST", format: "money" },
    { key: "sgstAmount", label: "SGST", format: "money" },
    { key: "igstAmount", label: "IGST", format: "money" },
    { key: "schemeDiscount", label: "Scheme Discount", format: "money" },
    { key: "cashDiscount", label: "Cash Discount", format: "money" },
    { key: "revenue", label: "Total (incl. tax)", format: "money" },
    { key: "roundOff", label: "Round Off", format: "signedMoney" },
    { key: "roundedTotal", label: "Rounded Total", format: "money" },
  ],
};

export const billWisePaymentsConfig = {
  title: "Bill Wise Payment Tracker",
  endpoint: "/api/v1/reports/bill-wise-payments",
  dateFilter: "optional",
  subtitle: "Every sales invoice with what's been paid against it and what's still outstanding, newest first.",
  columns: [
    { key: "invoiceNumber", label: "Invoice #" },
    { key: "invoiceDate", label: "Date" },
    { key: "customerName", label: "Customer" },
    { key: "status", label: "Status" },
    { key: "billAmount", label: "Bill Amount", format: "money" },
    { key: "amountPaid", label: "Amount Paid", format: "money" },
    { key: "balanceDue", label: "Balance Due", format: "money" },
    { key: "ageingDays", label: "Ageing (Days)" },
  ],
};

export const creditNotesConfig = {
  title: "Credit Notes",
  endpoint: "/api/v1/reports/credit-notes",
  dateFilter: "optional",
  subtitle: "Goods returned by customers and the credit value against each, newest first. Built from delivery return records.",
  columns: [
    { key: "returnedDate", label: "Date" },
    { key: "invoiceNumber", label: "Invoice #" },
    { key: "customerName", label: "Customer" },
    { key: "productName", label: "Product" },
    { key: "returnedQuantity", label: "Qty Returned", format: "qty" },
    { key: "unitPrice", label: "Unit Price", format: "money" },
    { key: "creditAmount", label: "Credit Amount", format: "money" },
    { key: "reasonDescription", label: "Reason" },
  ],
};

export const salesmanWiseSalesConfig = {
  title: "Salesman-wise Sales",
  endpoint: "/api/v1/reports/salesman-wise-sales",
  dateFilter: "optional",
  subtitle: "Sales per salesman (the salesman of the invoice's beat), with basic amount, GST split, discounts, total and round-off.",
  columns: [
    { key: "salesPersonName", label: "Salesman" },
    { key: "invoiceCount", label: "Invoices" },
    { key: "basicAmount", label: "Basic Amount (before tax)", format: "money" },
    { key: "cgstAmount", label: "CGST", format: "money" },
    { key: "sgstAmount", label: "SGST", format: "money" },
    { key: "igstAmount", label: "IGST", format: "money" },
    { key: "schemeDiscount", label: "Scheme Discount", format: "money" },
    { key: "cashDiscount", label: "Cash Discount", format: "money" },
    { key: "revenue", label: "Total (incl. tax)", format: "money" },
    { key: "roundOff", label: "Round Off", format: "signedMoney" },
    { key: "roundedTotal", label: "Rounded Total", format: "money" },
  ],
};

export const productWiseReturnsConfig = {
  title: "Product Wise Return / Cancel",
  endpoint: "/api/v1/reports/product-wise-returns",
  dateFilter: "optional",
  filters: ["product", "customer"],
  subtitle:
    "Quantity and value of goods returned or cancelled per product (credit note date), highest value first. Part returns and full cancellations / returns are also shown separately.",
  columns: [
    { key: "skuCode", label: "Product Code" },
    { key: "productName", label: "Product Name" },
    { key: "quantity", label: "Qty", format: "qty" },
    { key: "revenue", label: "Revenue", format: "money" },
    { key: "returnedQuantity", label: "Part Return Qty", format: "qty" },
    { key: "returnedValue", label: "Part Return Value", format: "money" },
    { key: "cancelledQuantity", label: "Full Cancel / Return Qty", format: "qty" },
    { key: "cancelledValue", label: "Full Cancel / Return Value", format: "money" },
  ],
};

export const schemeDiscountConfig = {
  title: "Scheme Discount",
  endpoint: "/api/v1/reports/scheme-discounts",
  dateFilter: "optional",
  dateLabels: ["Invoice date from", "to"],
  filters: ["customer", "product"],
  subtitle:
    "Scheme discounts given per bill line: Cash (scheme amount off the line) and Free Piece (goods given free on a scheme, valued at the billed or selling price).",
  columns: [
    { key: "invoiceNumber", label: "Bill No" },
    { key: "invoiceDate", label: "Bill Date" },
    { key: "customerCode", label: "Customer Code" },
    { key: "customerName", label: "Customer Name" },
    { key: "skuCode", label: "Product Code" },
    { key: "productName", label: "Product Name" },
    { key: "quantity", label: "Qty", format: "qty" },
    { key: "discountAmount", label: "Discount Amount", format: "money" },
    { key: "discountPercent", label: "Discount %", format: "qty", total: false },
    { key: "discountType", label: "Discount Type" },
    { key: "schemeName", label: "Scheme" },
  ],
};

export const stockLowConfig = {
  title: "Stock Low",
  endpoint: "/api/v1/inventory/reports/stock-low",
  dateFilter: "optional",
  dateLabels: ["From", "Stock as on"],
  filters: ["product", "warehouse"],
  subtitle:
    "Products below their minimum stock level (set on the product; the low-stock threshold in Receivables → Settings when none). Stock is as on the To date (today when blank), over the good warehouses unless one is picked.",
  columns: [
    { key: "skuCode", label: "Product Code" },
    { key: "productName", label: "Product Name" },
    { key: "uom", label: "UOM" },
    { key: "currentStock", label: "Current Stock", format: "qty" },
    { key: "minimumStockLevel", label: "Minimum Stock Level", format: "qty" },
    { key: "difference", label: "Difference (Current − Minimum)", format: "qty" },
  ],
};

export const stockExcessConfig = {
  title: "Stock Excess",
  endpoint: "/api/v1/inventory/reports/stock-excess",
  dateFilter: "optional",
  dateLabels: ["Sales from", "to (stock as on)"],
  filters: ["product", "warehouse"],
  subtitle:
    "Products with more stock than was sold on sales invoices in the period (the last 15 days when no dates are picked). The maximum stock level is the product's own, or the period's sales when none is set.",
  columns: [
    { key: "skuCode", label: "Product Code" },
    { key: "productName", label: "Product Name" },
    { key: "uom", label: "UOM" },
    { key: "currentStock", label: "Current Stock", format: "qty" },
    { key: "soldQuantity", label: "Sold in Period", format: "qty" },
    { key: "maximumStockLevel", label: "Maximum Stock Level", format: "qty" },
    { key: "difference", label: "Difference (Current − Maximum)", format: "qty" },
  ],
};
